import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppRegistryService } from '../../../core/services/app-registry.service';

interface AccessChildOption {
  _id: string;
  label: string;
  access?: string | null;
}

interface AccessModule {
  _id: string;
  label: string;
  access?: string | null;
  children: AccessChildOption[];
}

interface AccessPlatform {
  _id: string;
  label: string;
  icon?: string;
  access?: string | null;
  children: AccessModule[];
}

interface AccessLevel {
  label: string;
  value: string;
}

const DEFAULT_ACCESS_LEVELS: AccessLevel[] = [
  { label: 'Read Write', value: 'read_write' },
  { label: 'Read Only', value: 'read_only' },
  { label: 'No Access', value: 'no_access' },
];

const PLATFORMS: AccessPlatform[] = [
  {
    _id: 'security-command-center',
    label: 'Security Command Center',
    icon: 'security-command-center',
    access: null,
    children: [
      {
        _id: 'executive-view',
        label: 'Executive View',
        access: null,
        children: [
          {
            _id: 'executive-dashboard',
            label: 'Dashboard',
            access: null,
          },
        ],
      },
      {
        _id: 'my-workspace',
        label: 'My Workspace',
        access: null,
        children: [
          { _id: 'my-dashboard', label: 'My Dashboard', access: null },
          { _id: 'my-tasks', label: 'My Tasks', access: null },
          { _id: 'my-action-items', label: 'My Action Items', access: null },
          { _id: 'my-bookmarks', label: 'My Bookmarks', access: null },
        ],
      },
      {
        _id: 'control-and-task-operations',
        label: 'Control & Task Operations',
        access: null,
        children: [
          { _id: 'task-dashboard', label: 'Task Dashboard', access: null },
          { _id: 'all-tasks', label: 'All Tasks', access: null },
          { _id: 'all-action-items', label: 'All Action Items', access: null },
          { _id: 'evidence-vault', label: 'Evidence Vault', access: null },
          { _id: 'assets-list', label: 'Assets', access: null },
        ],
      },
      {
        _id: 'security-overview',
        label: 'Security Overview',
        access: null,
        children: [
          {
            _id: 'security-landscape',
            label: 'Security Landscape',
            access: null,
          },
        ],
      },
      {
        _id: 'scc-logs',
        label: 'Logs',
        access: null,
        children: [
          { _id: 'scc-activity-logs', label: 'Activity Logs', access: null },
        ],
      },
    ],
  },
  {
    _id: 'dmarc-management',
    label: 'DMARC Management (eDMARC)',
    icon: 'dmarc-management',
    access: null,
    children: [
      {
        _id: 'dmarc-overview',
        label: 'Overview',
        access: null,
        children: [
          { _id: 'dmarc-dashboard', label: 'Dashboard', access: null },
          { _id: 'dmarc-domain', label: 'Domain', access: null },
        ],
      },
      {
        _id: 'dmarc-analysis',
        label: 'Analysis',
        access: null,
        children: [
          { _id: 'dmarc-investigate', label: 'Investigate', access: null },
          { _id: 'dmarc-email-threat', label: 'Email Threat', access: null },
        ],
      },
      {
        _id: 'dmarc-reports',
        label: 'Reports',
        access: null,
        children: [
          { _id: 'dmarc-reports-view', label: 'DMARC Reports', access: null },
          { _id: 'dmarc-issues', label: 'Issues', access: null },
        ],
      },
      {
        _id: 'dmarc-tools',
        label: 'Tools',
        access: null,
        children: [
          { _id: 'dmarc-dns-records', label: 'DNS Records', access: null },
          { _id: 'dmarc-spf-generator', label: 'SPF Generator', access: null },
          {
            _id: 'dmarc-dmarc-generator',
            label: 'DMARC Generator',
            access: null,
          },
          {
            _id: 'dmarc-bimi-verification',
            label: 'BIMI Verification',
            access: null,
          },
        ],
      },
    ],
  },
  {
    _id: 'attack-surface-management',
    label: 'Attack Surface Management (ASM)',
    icon: 'attack-surface-management',
    access: null,
    children: [
      {
        _id: 'asm-overview',
        label: 'Overview',
        access: null,
        children: [{ _id: 'asm-dashboard', label: 'Dashboard', access: null }],
      },
      {
        _id: 'asm-attack-surface',
        label: 'Attack Surface',
        access: null,
        children: [
          { _id: 'asm-summary', label: 'Summary', access: null },
          { _id: 'asm-targets', label: 'Targets', access: null },
          { _id: 'asm-assets', label: 'Assets', access: null },
          { _id: 'asm-subdomains', label: 'Subdomains', access: null },
          { _id: 'asm-ip-open-ports', label: 'IP & Open Ports', access: null },
          {
            _id: 'asm-ssl-certificates',
            label: 'SSL Certificates',
            access: null,
          },
          { _id: 'asm-scan-results', label: 'Scan Results', access: null },
        ],
      },
      {
        _id: 'asm-email-security',
        label: 'Email Security',
        access: null,
        children: [
          { _id: 'asm-email-summary', label: 'Summary', access: null },
          {
            _id: 'asm-spf-chain-analysis',
            label: 'SPF Chain Analysis',
            access: null,
          },
          { _id: 'asm-dmarc-analysis', label: 'DMARC Analysis', access: null },
        ],
      },
      {
        _id: 'asm-shadow-saas',
        label: 'Shadow SaaS',
        access: null,
        children: [{ _id: 'asm-detection', label: 'Detection', access: null }],
      },
      {
        _id: 'asm-risk-intelligence',
        label: 'Risk Intelligence',
        access: null,
        children: [
          {
            _id: 'asm-passive-vuln-scanning',
            label: 'Passive Vulnerability Scanning',
            access: null,
          },
          {
            _id: 'asm-active-vuln-scanning',
            label: 'Active Vulnerability Scanning',
            access: null,
          },
          {
            _id: 'asm-informational-findings',
            label: 'Informational Findings',
            access: null,
          },
          {
            _id: 'asm-web-technologies',
            label: 'Web Technologies',
            access: null,
          },
        ],
      },
      {
        _id: 'asm-reports',
        label: 'Reports',
        access: null,
        children: [{ _id: 'asm-export', label: 'Export', access: null }],
      },
    ],
  },
  {
    _id: 'mradar',
    label: 'MRadar',
    icon: 'mradar',
    access: null,
    children: [
      {
        _id: 'mradar-overview',
        label: 'Overview',
        access: null,
        children: [
          { _id: 'mradar-dashboard', label: 'Dashboard', access: null },
        ],
      },
      {
        _id: 'mradar-inventory',
        label: 'Inventory',
        access: null,
        children: [
          { _id: 'mradar-assets', label: 'Assets', access: null },
          { _id: 'mradar-endpoints', label: 'Endpoints', access: null },
          { _id: 'mradar-owners', label: 'Owners', access: null },
        ],
      },
      {
        _id: 'mradar-security',
        label: 'Security',
        access: null,
        children: [
          { _id: 'mradar-software', label: 'Software', access: null },
          {
            _id: 'mradar-vulnerabilities',
            label: 'Vulnerabilities',
            access: null,
          },
          { _id: 'mradar-sam', label: 'SAM', access: null },
        ],
      },
      {
        _id: 'mradar-admin',
        label: 'Admin',
        access: null,
        children: [
          { _id: 'mradar-audit-logs', label: 'Audit Logs', access: null },
        ],
      },
    ],
  },
  {
    _id: 'supply-chain-resilience',
    label: 'Supply Chain Resilience (SCR)',
    icon: 'supply-chain-resilience',
    access: null,
    children: [
      {
        _id: 'scr-analysis',
        label: 'Analysis',
        access: null,
        children: [
          { _id: 'scr-dashboard', label: 'Dashboard', access: null },
          { _id: 'scr-sbom', label: 'SBOM', access: null },
          { _id: 'scr-sca', label: 'SCA', access: null },
          { _id: 'scr-secrets', label: 'Secrets', access: null },
          { _id: 'scr-ai-bom', label: 'AI-BOM', access: null },
          { _id: 'scr-release-diff', label: 'Release Diff', access: null },
          {
            _id: 'scr-drift-detection',
            label: 'Drift Detection',
            access: null,
          },
        ],
      },
      {
        _id: 'scr-scan',
        label: 'Scan',
        access: null,
        children: [
          {
            _id: 'scr-scan-repositories',
            label: 'Scan Repositories',
            access: null,
          },
          {
            _id: 'scr-code-repositories',
            label: 'Code Repositories',
            access: null,
          },
          { _id: 'scr-repo-heatmap', label: 'Repo Heatmap', access: null },
        ],
      },
      {
        _id: 'scr-data',
        label: 'Data',
        access: null,
        children: [
          { _id: 'scr-export', label: 'Export', access: null },
          { _id: 'scr-scan-history', label: 'Scan History', access: null },
        ],
      },
      {
        _id: 'scr-configuration',
        label: 'Configuration',
        access: null,
        children: [
          { _id: 'integrations', label: 'Integrations', access: null },
        ],
      },
    ],
  },
  {
    _id: 'risk-resilience',
    label: 'Risk Resilience',
    icon: 'risk-resilience',
    access: null,
    children: [
      {
        _id: 'risk-impact-management',
        label: 'Risk and Impact Management',
        access: null,
        children: [
          { _id: 'risk-dashboard', label: 'Risk Dashboard', access: null },
        ],
      },
      {
        _id: 'risk-register',
        label: 'Risk Register',
        access: null,
        children: [
          { _id: 'system-risk', label: 'System Risk', access: null },
          { _id: 'manual-risk', label: 'Manual Risk', access: null },
        ],
      },
    ],
  },
  {
    _id: 'compliance-governance',
    label: 'Compliance and Governance',
    icon: 'compliance-governance',
    access: null,
    children: [
      {
        _id: 'compliance-governance-module',
        label: 'Compliance and Governance',
        access: null,
        children: [
          { _id: 'compliance-dashboard', label: 'Dashboard', access: null },
        ],
      },
      {
        _id: 'compliance-library',
        label: 'Library',
        access: null,
        children: [
          {
            _id: 'framework-library',
            label: 'Framework Library',
            access: null,
          },
          { _id: 'evidence-library', label: 'Evidence Library', access: null },
        ],
      },
    ],
  },
  {
    _id: 'vulnerability-management',
    label: 'Vulnerability Management (EVM)',
    icon: 'vulnerability-management',
    access: null,
    children: [
      {
        _id: 'vm-vulnerability-management',
        label: 'Vulnerability Management',
        access: null,
        children: [
          { _id: 'vm-dashboard', label: 'Dashboard', access: null },
          { _id: 'vm-findings', label: 'Findings', access: null },
          { _id: 'vm-sla-tracker', label: 'SLA Tracker', access: null },
          {
            _id: 'vm-risk-acceptance',
            label: 'Risk Acceptance',
            access: null,
          },
        ],
      },
      {
        _id: 'vm-bug-bounty',
        label: 'Bug Bounty',
        access: null,
        children: [
          {
            _id: 'vm-bug-bounty-dashboard',
            label: 'Dashboard',
            access: null,
          },
          { _id: 'vm-programs', label: 'Programs', access: null },
          { _id: 'vm-submissions', label: 'Submissions', access: null },
          { _id: 'vm-researchers', label: 'Researchers', access: null },
        ],
      },
      {
        _id: 'vm-pentest-management',
        label: 'Pentest Management',
        access: null,
        children: [
          {
            _id: 'vm-pentest-dashboard',
            label: 'Dashboard',
            access: null,
          },
          { _id: 'vm-projects', label: 'Projects', access: null },
          { _id: 'vm-methodologies', label: 'Methodologies', access: null },
          { _id: 'vm-attack-chains', label: 'Attack Chains', access: null },
        ],
      },
      {
        _id: 'vm-import-hub',
        label: 'Import Hub',
        access: null,
        children: [
          { _id: 'vm-import-now', label: 'Import Now', access: null },
          { _id: 'vm-scan-formats', label: 'Scan Formats', access: null },
          { _id: 'vm-import-history', label: 'Import History', access: null },
        ],
      },
      {
        _id: 'vm-reports',
        label: 'Reports',
        access: null,
        children: [
          { _id: 'vm-reports-overview', label: 'Overview', access: null },
          { _id: 'vm-reports-compliance', label: 'Compliance', access: null },
          { _id: 'vm-reports-generate', label: 'Generate', access: null },
          { _id: 'vm-saved-reports', label: 'Saved Reports', access: null },
        ],
      },
    ],
  },
  {
    _id: 'integrations-hub',
    label: 'Integrations Hub',
    icon: 'security-integrations-hub',
    access: null,
    children: [
      {
        _id: 'integration-marketplace',
        label: 'Integration Marketplace',
        access: null,
        children: [],
      },
    ],
  },
  {
    _id: 'settings',
    label: 'Settings',
    icon: 'configurations',
    access: null,
    children: [
      {
        _id: 'settings-module',
        label: 'Settings',
        access: null,
        children: [
          { _id: 'organization', label: 'Organization', access: null },
          { _id: 'employees', label: 'Employees', access: null },
          {
            _id: 'access-management',
            label: 'Access Management',
            access: null,
          },
          { _id: 'my-profile', label: 'My Profile', access: null },
        ],
      },
      {
        _id: 'applications',
        label: 'Applications',
        access: null,
        children: [
          { _id: 'personal-app', label: 'Personal App', access: null },
        ],
      },
    ],
  },
];

@Component({
  selector: 'app-access-view-card',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './access-view-card.component.html',
  styleUrl: './access-view-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessViewCardComponent implements OnInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() activeModalContext: any;

  expandedRows: { [key: string]: boolean } = {};
  collapsedRows: { [key: string]: boolean } = {};
  selectedAccess: Record<string, string> = {};
  allExpanded = false;
  private iconCache: Record<string, SafeHtml> = {};
  constructor(
    private sanitizer: DomSanitizer,
    private appRegistryService: AppRegistryService,
  ) {}
  ngOnInit(): void {
    this.initializeMatrix();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['formGroup']) {
      this.initializeMatrix();
    }
  }

  readonly platforms = PLATFORMS;
  accessLevels: AccessLevel[] = DEFAULT_ACCESS_LEVELS;
  fieldName = 'access';
  defaultAccessValue = 'read_only';

  expandAll(): void {
    this.allExpanded = true;
    this.platforms.forEach((platform) => {
      this.expandedRows[platform._id] = true;
      delete this.collapsedRows[platform._id];
      platform.children.forEach((mod) => {
        this.expandedRows[mod._id] = true;
        delete this.collapsedRows[mod._id];
      });
    });
  }

  collapseAll(): void {
    this.allExpanded = false;
    this.platforms.forEach((platform) => {
      delete this.expandedRows[platform._id];
      this.collapsedRows[platform._id] = true;
      platform.children.forEach((mod) => {
        delete this.expandedRows[mod._id];
        this.collapsedRows[mod._id] = true;
      });
    });
  }

  getAccessCount(value: string): number {
    let count = 0;
    this.platforms.forEach((platform) => {
      platform.children.forEach((mod) => {
        if (mod.children && mod.children.length > 0) {
          mod.children.forEach((child) => {
            const key = this.rowKey(platform._id, mod._id, child._id);
            if (this.selectedAccess[key] === value) {
              count++;
            }
          });
        } else {
          const key = this.rowKey(platform._id, mod._id);
          if (this.selectedAccess[key] === value) {
            count++;
          }
        }
      });
    });
    return count;
  }

  formatCount(count: number): string {
    return count < 10 ? '0' + count : '' + count;
  }

  initializeMatrix(): void {
    this.configureMatrix();

    this.platforms.forEach((platform) => {
      if (this.expandedRows[platform._id] === undefined) {
        this.expandedRows[platform._id] = false;
      }
    });

    const formValue = this.formGroup?.get(this.fieldName)?.value;
    const controlValue =
      formValue === undefined || formValue === null || formValue === ''
        ? this.activeModalContext?.[this.fieldName]
        : formValue;
    this.selectedAccess = this.normalizeValue(controlValue);
    this.applyDefaultAccess();
    this.syncAllParentAccess();
    this.updateControlValue(false);
  }

  trackByPlatformId(_: number, platform: AccessPlatform): string {
    return platform._id;
  }

  trackByModuleId(_: number, module: AccessModule): string {
    return module._id;
  }

  trackByChildId(_: number, child: AccessChildOption): string {
    return child._id;
  }

  trackByAccessValue(_: number, level: AccessLevel): string {
    return level.value;
  }

  hasModules(platform: AccessPlatform): boolean {
    return Array.isArray(platform.children) && platform.children.length > 0;
  }

  hasChildren(module: AccessModule): boolean {
    return Array.isArray(module.children) && module.children.length > 0;
  }

  toggleRow(item: any): void {
    const id = item._id;
    if (this.isExpanded(item)) {
      delete this.expandedRows[id];
      this.collapsedRows[id] = true;
    } else {
      this.expandedRows[id] = true;
      delete this.collapsedRows[id];
    }
  }

  isExpanded(item: any): boolean {
    const id = item._id;
    if (this.collapsedRows[id]) return false;
    if (this.expandedRows[id]) return true;
    return false;
  }

  rowKey(platformId: string, moduleId?: string, childId?: string): string {
    if (childId && moduleId) return `${platformId}.${moduleId}.${childId}`;
    if (moduleId) return `${platformId}.${moduleId}`;
    return platformId;
  }

  // Platform-level access selection
  selectPlatformAccess(
    event: MouseEvent,
    platform: AccessPlatform,
    accessValue: string,
  ): void {
    const key = this.rowKey(platform._id);
    if (this.selectedAccess[key] === accessValue) {
      event.preventDefault();
      delete this.selectedAccess[key];
      platform.children.forEach((mod) => {
        delete this.selectedAccess[this.rowKey(platform._id, mod._id)];
        mod.children.forEach((child) => {
          delete this.selectedAccess[
            this.rowKey(platform._id, mod._id, child._id)
          ];
        });
      });
    } else {
      this.selectedAccess[key] = accessValue;
      platform.children.forEach((mod) => {
        this.selectedAccess[this.rowKey(platform._id, mod._id)] = accessValue;
        mod.children.forEach((child) => {
          this.selectedAccess[this.rowKey(platform._id, mod._id, child._id)] =
            accessValue;
        });
      });
    }
    this.updateControlValue();
  }

  // Module-level access selection
  selectModuleAccess(
    event: MouseEvent,
    platform: AccessPlatform,
    module: AccessModule,
    accessValue: string,
  ): void {
    const key = this.rowKey(platform._id, module._id);
    if (this.selectedAccess[key] === accessValue) {
      event.preventDefault();
      delete this.selectedAccess[key];
      module.children.forEach((child) => {
        delete this.selectedAccess[
          this.rowKey(platform._id, module._id, child._id)
        ];
      });
    } else {
      this.selectedAccess[key] = accessValue;
      module.children.forEach((child) => {
        this.selectedAccess[this.rowKey(platform._id, module._id, child._id)] =
          accessValue;
      });
    }
    this.syncPlatformAccess(platform);
    this.updateControlValue();
  }

  // Child-level access selection
  selectChildAccess(
    event: MouseEvent,
    platform: AccessPlatform,
    module: AccessModule,
    child: AccessChildOption,
    accessValue: string,
  ): void {
    const key = this.rowKey(platform._id, module._id, child._id);
    if (this.selectedAccess[key] === accessValue) {
      event.preventDefault();
      delete this.selectedAccess[key];
    } else {
      this.selectedAccess[key] = accessValue;
    }
    this.syncModuleAccess(platform, module);
    this.syncPlatformAccess(platform);
    this.updateControlValue();
  }

  isPlatformSelected(platform: AccessPlatform, accessValue: string): boolean {
    return this.selectedAccess[this.rowKey(platform._id)] === accessValue;
  }

  isModuleSelected(
    platform: AccessPlatform,
    module: AccessModule,
    accessValue: string,
  ): boolean {
    return (
      this.selectedAccess[this.rowKey(platform._id, module._id)] === accessValue
    );
  }

  isChildSelected(
    platform: AccessPlatform,
    module: AccessModule,
    child: AccessChildOption,
    accessValue: string,
  ): boolean {
    return (
      this.selectedAccess[this.rowKey(platform._id, module._id, child._id)] ===
      accessValue
    );
  }

  getIcon(icon?: string): SafeHtml {
    const iconKey = icon ?? '';

    if (this.iconCache[iconKey]) {
      return this.iconCache[iconKey];
    }

    const app = this.appRegistryService.apps.find((a) => {
      const appLabel = a.label.toLowerCase().replace(/-/g, ' ');
      const cleanKey = iconKey.toLowerCase().replace(/-/g, ' ');
      return (
        appLabel.includes(cleanKey) ||
        (cleanKey.includes(appLabel) && !cleanKey.includes('integrations')) ||
        (cleanKey.includes('scc') &&
          appLabel.includes('security command center')) ||
        (cleanKey.includes('security') &&
          !cleanKey.includes('integrations') &&
          appLabel.includes('security command center')) ||
        (cleanKey.includes('risk') && appLabel.includes('risk')) ||
        (cleanKey.includes('compliance') && appLabel.includes('compliance')) ||
        (cleanKey.includes('dmarc') && appLabel.includes('dmarc')) ||
        (cleanKey.includes('radar') && appLabel.includes('radar')) ||
        (cleanKey.includes('supply chain') && appLabel.includes('supply chain'))
      );
    });

    if (app && app.iconPath) {
      const imgHtml = `<img src="${app.iconPath}" class="w-full h-full object-contain" alt="${app.label}" />`;
      this.iconCache[iconKey] = this.sanitizer.bypassSecurityTrustHtml(imgHtml);
      return this.iconCache[iconKey];
    }

    const iconMap: Record<string, string> = {
      'security-command-center': `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
        />
      </svg>
    `,
      'dmarc-management': `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"/>
          <path d="m22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7M16 19h6m-3-3v6"/>
        </g>
      </svg>
    `,
      'attack-surface-management': `<img src="assets/app-icons/easm.svg" class="w-full h-full object-contain" alt="Attack Surface Management" />`,
      'vulnerability-management': `<img src="assets/app-icons/evm.svg" class="w-full h-full object-contain" alt="Vulnerability Management" />`,
      mradar: `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"/><path d="M4 6h.01"/><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"/>
          <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"/><path d="M12 18h.01"/><path d="M17.99 11.66A6 6 0 0 1 15.77 16.22"/>
          <circle cx="12" cy="12" r="2"/><path d="m13.41 10.59l5.66-5.66"/>
        </g>
      </svg>
    `,
      'supply-chain-resilience': `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2"/>
          <path d="M8.5 2h7M7 16.5h10"/>
        </g>
      </svg>
    `,
      'risk-resilience': `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M21 11c0 5.55-3.84 10.74-9 12c-5.16-1.26-9-6.45-9-12V5l9-4l9 4zm-9 10c3.75-1 7-5.46 7-9.78V6.3l-7-3.12L5 6.3v4.92C5 15.54 8.25 20 12 21M11 7h2v6h-2zm0 8h2v2h-2z"
        />
      </svg>
    `,
      'compliance-governance': `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M16.757 9.303a.75.75 0 0 0-1.014-1.106l-5.47 5.015L8.28 11.22a.75.75 0 0 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.037.023zM20.25 5c-2.663 0-5.258-.943-7.8-2.85a.75.75 0 0 0-.9 0C9.008 4.057 6.413 5 3.75 5a.75.75 0 0 0-.75.75V11c0 5.001 2.958 8.676 8.725 10.948a.75.75 0 0 0 .55 0C18.042 19.676 21 16 21 11V5.75a.75.75 0 0 0-.75-.75"
        />
      </svg>
    `,
      'digital-asset-intelligence': `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1">
          <path stroke-width="1.5" d="M19 4H5c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 5.602 2 6.068 2 7s0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 10 4.068 10 5 10h14c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 8.398 22 7.932 22 7s0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 4 19.932 4 19 4m0 10H5c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 15.602 2 16.068 2 17s0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 20 4.068 20 5 20h14c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 18.398 22 17.932 22 17s0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 14 19.932 14 19 14"/>
          <path stroke-width="2" d="M6 17h.01M10 17h.01M6 7h.01M10 7h.01"/>
        </g>
      </svg>`,
      'security-integrations-hub': `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1934_74401)"><path d="M3.40515 0.392932C4.12871 0.200431 4.89065 0.202367 5.61315 0.398791C6.33544 0.595239 6.99303 0.978909 7.5194 1.5111C8.04589 2.04343 8.42277 2.70543 8.6112 3.43004L11.2069 0.835315C11.582 0.460587 12.0907 0.250207 12.621 0.250354C13.1514 0.250467 13.66 0.461192 14.035 0.836291L16.1415 2.94762C16.1991 3.00549 16.2417 3.07655 16.2655 3.15465C16.2893 3.23305 16.293 3.31644 16.2772 3.39684C16.2614 3.4771 16.2263 3.55213 16.1747 3.61559C16.123 3.67905 16.0567 3.72931 15.9813 3.7611C15.4905 3.96793 15.0576 4.29221 14.7216 4.70543C14.3854 5.11883 14.1559 5.60933 14.0536 6.13219C13.9513 6.65494 13.9792 7.19497 14.1346 7.70446C14.2901 8.21397 14.5685 8.67738 14.9452 9.05406C15.3219 9.43075 15.7852 9.70918 16.2948 9.86461C16.8043 10.02 17.3443 10.0479 17.8671 9.94567C18.3898 9.84334 18.8795 9.61371 19.2928 9.2777C19.7062 8.94159 20.0313 8.50891 20.2382 8.01793C20.2699 7.94267 20.3194 7.8762 20.3827 7.82457C20.4462 7.77284 20.522 7.73686 20.6024 7.72106C20.6828 7.70528 20.7662 7.70993 20.8446 7.73375C20.9227 7.75755 20.9938 7.80017 21.0516 7.85778L23.1639 9.96422C23.3496 10.1498 23.497 10.3701 23.5975 10.6127C23.698 10.8553 23.7498 11.1157 23.7499 11.3783C23.7499 11.6409 23.698 11.9013 23.5975 12.1439C23.497 12.3866 23.3497 12.6076 23.1639 12.7933L20.5692 15.3871C21.5 15.6295 22.3212 16.1811 22.8983 16.9505C23.4752 17.7199 23.7739 18.6622 23.746 19.6234C23.718 20.5849 23.3649 21.5089 22.744 22.2435C22.1231 22.9781 21.2713 23.4805 20.328 23.6683C20.0909 23.7155 19.8518 23.74 19.6132 23.7464C20.467 22.9275 20.9999 21.7759 20.9999 20.4994C20.9997 18.0143 18.985 15.9995 16.4999 15.9994C14.0147 15.9994 12.0001 18.0143 11.9999 20.4994C11.9999 21.4744 12.3108 22.3767 12.8378 23.1136L12.7919 23.1605C12.6063 23.3461 12.3859 23.4936 12.1434 23.5941C11.9008 23.6946 11.6404 23.7464 11.3778 23.7464C11.1152 23.7464 10.8548 23.6946 10.6122 23.5941C10.3695 23.4936 10.1485 23.3463 9.96276 23.1605L7.85729 21.0531C7.79942 20.9951 7.75612 20.9235 7.73229 20.8451C7.70859 20.7669 7.70485 20.6841 7.72058 20.6039C7.73638 20.5235 7.77139 20.4477 7.82311 20.3841C7.87483 20.3206 7.94196 20.2714 8.01745 20.2396C8.50828 20.0327 8.9402 19.7076 9.27624 19.2943C9.61233 18.8809 9.84186 18.3914 9.94421 17.8685C10.0465 17.3457 10.0186 16.8058 9.86315 16.2963C9.70772 15.7866 9.42934 15.3224 9.05261 14.9457C8.67595 14.5691 8.21241 14.2905 7.703 14.1351C7.19347 13.9797 6.6535 13.9527 6.13073 14.055C5.60803 14.1574 5.11827 14.3861 4.70495 14.722C4.2916 15.0581 3.96653 15.4909 3.75964 15.9818C3.72797 16.0574 3.67856 16.1243 3.61511 16.1761C3.55153 16.228 3.47586 16.2638 3.39538 16.2796C3.31491 16.2954 3.23164 16.2909 3.15319 16.267C3.07468 16.243 3.00318 16.2 2.94518 16.142L0.834833 14.0365C0.64903 13.8508 0.5018 13.6298 0.401239 13.3871C0.300708 13.1444 0.248896 12.8841 0.248896 12.6214C0.248922 12.3588 0.300705 12.0985 0.401239 11.8558C0.501762 11.6133 0.649182 11.393 0.834833 11.2074L3.42858 8.61266C2.70411 8.42432 2.04197 8.04812 1.50964 7.52184C0.977344 6.99553 0.593891 6.33788 0.397333 5.61559C0.200769 4.89312 0.198136 4.13118 0.390497 3.40758C0.58288 2.68408 0.962741 2.02392 1.49206 1.49449C2.02143 0.965044 2.68162 0.585429 3.40515 0.392932Z" fill="#FFD700"/><path d="M15.5809 22.7516L13.4434 20.6141L13.9777 20.0797L15.5809 21.6828L19.0215 18.2422L19.5559 18.7766L15.5809 22.7516Z" fill="#A1A1A1"/></g><defs><clipPath id="clip0_1934_74401"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`,
      configurations: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
      settings: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
    };

    if (iconMap[iconKey]) {
      this.iconCache[iconKey] = this.sanitizer.bypassSecurityTrustHtml(
        iconMap[iconKey],
      );
      return this.iconCache[iconKey];
    }

    const icons = `
    <i class="ki-filled ki-element-11"></i>
  `;
    this.iconCache[iconKey] = this.sanitizer.bypassSecurityTrustHtml(icons);
    return this.iconCache[iconKey];
  }

  private configureMatrix(): void {
    this.accessLevels = this.fields?.options?.accessLevels?.length
      ? this.fields.options.accessLevels
      : DEFAULT_ACCESS_LEVELS;
    this.fieldName = this.fields?.fieldName ?? 'access';
    this.defaultAccessValue =
      this.fields?.defaultValue ??
      this.accessLevels.find((level) => level.value === 'read_only')?.value ??
      this.accessLevels[0]?.value ??
      '';
  }

  private normalizeValue(value: any): Record<string, string> {
    if (!value) {
      return {};
    }

    if (value?.[this.fieldName]) {
      return this.normalizeValue(value[this.fieldName]);
    }

    if (this.fieldName !== 'access' && value?.access) {
      return this.normalizeValue(value.access);
    }

    if (!Array.isArray(value)) {
      if (typeof value !== 'object') {
        return {};
      }
      return Object.entries(value).reduce(
        (result: Record<string, string>, [key, accessValue]) => {
          if (typeof accessValue === 'string') {
            result[key] = accessValue;
          }
          return result;
        },
        {},
      );
    }

    // Handle 3-level array (platforms → children → children)
    return value.reduce((result: Record<string, string>, platformItem: any) => {
      const platformId = platformItem?._id ?? platformItem?.platformId;
      if (platformId && platformItem?.access) {
        result[platformId] = platformItem.access;
      }

      (platformItem?.children ?? platformItem?.children ?? []).forEach(
        (modItem: any) => {
          const moduleId = modItem?._id ?? modItem?.moduleId;
          if (platformId && moduleId && modItem?.access) {
            result[this.rowKey(platformId, moduleId)] = modItem.access;
          }

          (modItem?.children ?? []).forEach((child: any) => {
            const childId = child?._id ?? child?.moduleId;
            if (platformId && moduleId && childId && child?.access) {
              result[this.rowKey(platformId, moduleId, childId)] = child.access;
            }
          });
        },
      );

      return result;
    }, {});
  }

  private applyDefaultAccess(): void {
    const defaultAccess = this.defaultAccessValue;
    if (!defaultAccess) return;

    this.platforms.forEach((platform) => {
      platform.children.forEach((mod) => {
        mod.children.forEach((child) => {
          const childKey = this.rowKey(platform._id, mod._id, child._id);
          if (!this.selectedAccess[childKey]) {
            this.selectedAccess[childKey] =
              child.access ?? mod.access ?? platform.access ?? defaultAccess;
          }
        });
        // For children with no children, apply default to module itself
        if (mod.children.length === 0) {
          const modKey = this.rowKey(platform._id, mod._id);
          if (!this.selectedAccess[modKey]) {
            this.selectedAccess[modKey] =
              mod.access ?? platform.access ?? defaultAccess;
          }
        }
      });
    });
  }

  private syncAllParentAccess(): void {
    this.platforms.forEach((platform) => {
      platform.children.forEach((mod) => this.syncModuleAccess(platform, mod));
      this.syncPlatformAccess(platform);
    });
  }

  private syncModuleAccess(
    platform: AccessPlatform,
    module: AccessModule,
  ): void {
    if (module.children.length === 0) return;

    const childAccessValues = module.children.map(
      (child) =>
        this.selectedAccess[this.rowKey(platform._id, module._id, child._id)],
    );
    const first = childAccessValues[0];
    const allMatch = !!first && childAccessValues.every((v) => v === first);
    const modKey = this.rowKey(platform._id, module._id);

    if (allMatch) {
      this.selectedAccess[modKey] = first;
    } else {
      delete this.selectedAccess[modKey];
    }
  }

  private syncPlatformAccess(platform: AccessPlatform): void {
    if (platform.children.length === 0) return;

    const moduleAccessValues = platform.children.map(
      (mod) => this.selectedAccess[this.rowKey(platform._id, mod._id)],
    );
    const first = moduleAccessValues[0];
    const allMatch = !!first && moduleAccessValues.every((v) => v === first);
    const platformKey = this.rowKey(platform._id);

    if (allMatch) {
      this.selectedAccess[platformKey] = first;
    } else {
      delete this.selectedAccess[platformKey];
    }
  }

  private updateControlValue(markDirty = true): void {
    const control = this.formGroup?.get(this.fieldName);
    if (!control) return;

    const value = this.platforms
      .map((platform) => {
        const platformAccess = this.selectedAccess[this.rowKey(platform._id)];
        const children = platform.children
          .map((mod) => {
            const moduleAccess =
              this.selectedAccess[this.rowKey(platform._id, mod._id)];
            const children = mod.children
              .map((child) => ({
                _id: child._id,
                label: child.label,
                access:
                  this.selectedAccess[
                    this.rowKey(platform._id, mod._id, child._id)
                  ],
              }))
              .filter((child) => !!child.access);

            return {
              _id: mod._id,
              label: mod.label,
              access: moduleAccess,
              children,
            };
          })
          .filter((mod) => !!mod.access || mod.children.length > 0);

        return {
          _id: platform._id,
          label: platform.label,
          access: platformAccess,
          children,
        };
      })
      .filter((platform) => !!platform.access || platform.children.length > 0);

    control.setValue(value, { emitEvent: markDirty });

    if (markDirty) {
      control.markAsDirty();
      control.markAsTouched();
    }
    control.updateValueAndValidity({ emitEvent: markDirty });
  }
}
